import { isApolloError } from '@apollo/client'
import { Button, Space, Typography } from 'antd'
import axios, { AxiosError } from 'axios'
import {
  notificationHandler,
  simpleErrorNotification,
} from './notificationHandler'
import { ParagraphProps } from 'antd/lib/typography/Paragraph'
const { Paragraph } = Typography

/**
 * Wraps the antd notification helper to provide a consistent interface for
 * handling client side errors
 *
 * @param error An error object or error message. The handler will check for an
 * Apollo Error, normal Error, and can even handle neither.
 * @param extraMessages any additional messages you want included with the notification
 * @param callback a callback function to run after the message is finished
 * @example
 * const messageDone = () => console.log('Done!')
 *
 * const { data, loading } = useSomeGraphQLQuery({
 *  variables: { someVariable: value },
 *  onError: (err) => errorHandler(
 *    err,
 *    ['You need additional permissions', 'Some more things you need to know'],
 *    messageDone
 *  ),
 * })
 *
 * // dead simple use
 * const { data } = useSomeOtherGraphQLQuery({
 *  variables: { anotherVariable: anotherValue },
 *  onError: errorHandler
 * })
 */
export function errorHandler(
  error: any,
  extraMessages?: string[],
  callback?: () => void
) {
  console.error(error)
  if (error?.response?.data?.startsWith?.('Proxy error')) return proxyError()
  else if (isApolloError(error)) {
    const { message, networkError, graphQLErrors } = error
    if (message.includes('Proxy')) return proxyError()
    else if (
      message === `Unexpected token '<', "<!DOCTYPE "... is not valid JSON` &&
      networkError &&
      'bodyText' in networkError
    ) {
      return htmlError(networkError.bodyText)
    }
    const messages: Message[] =
      extraMessages?.map((message) => ({ message })) ?? []
    messages.push(
      // https://github.com/apollographql/apollo-client/issues/2810
      // @ts-expect-error
      ...(networkError?.result?.errors?.map<Message>(({ message }) => ({
        message,
        copyable: true,
      })) ?? [])
    )
    messages.push(
      ...graphQLErrors.reduce<Message[]>((messages, { extensions }) => {
        if (!extensions) return messages
        delete extensions.code
        delete extensions.exception
        if (Object.keys(extensions).length)
          messages.push({
            message: JSON.stringify(extensions, null, 2),
            copyable: true,
          })
        return messages
      }, [])
    )
    return additionalDetails({ message, callback, messages })
  } else if (axios.isAxiosError(error)) {
    const { response, message: __message } = error as AxiosError<any>
    const { config, data } = response ?? {}
    const { url } = config ?? {}
    const { message: _message, extras } = data ?? {}
    const message = _message ?? __message ?? 'Unknown Error'
    const messages: Message[] =
      extraMessages?.map((message) => ({ message })) ?? []
    messages.push({
      message: JSON.stringify({ url, ...extras }, null, 2),
      copyable: true,
    })
    return additionalDetails({
      message,
      callback,
      messages,
    })
  } else if (error.toString().includes('DOCTYPE'))
    return htmlError(error.toString())
  return additionalDetails({
    message: error.toString(),
    callback,
    messages: extraMessages?.map((message) => ({ message })),
  })
}

function htmlError(htmlString?: string) {
  console.log({ htmlString })
  if (!htmlString)
    return simpleErrorNotification(
      'There was an error returned in HTML format that could not be parsed'
    )
  return notificationHandler({
    title: 'Error returned as HTML',
    alternateType: 'error',
    details: (
      <div style={{ overflow: 'auto', maxHeight: 300 }}>
        <div dangerouslySetInnerHTML={{ __html: htmlString }} />
      </div>
    ),
  })
}

const proxyError = () =>
  simpleErrorNotification(
    'There was an issue connecting to the server.',
    'It may be temporarily offline. Try again in a few minutes...'
  )

interface Message extends Pick<ParagraphProps, 'ellipsis' | 'copyable'> {
  message: string
}

const getDetails = (messages?: Message[]) =>
  !messages?.length ? null : (
    <Typography.Paragraph>
      {messages.map(({ message, copyable, ellipsis }) => (
        <Typography.Paragraph copyable={copyable} ellipsis={ellipsis}>
          {message}
        </Typography.Paragraph>
      ))}
    </Typography.Paragraph>
  )

interface AdditionalDetailsProps {
  message: string
  messages?: Message[]
  callback?: () => void
}

function additionalDetails({
  message,
  messages,
  callback,
}: AdditionalDetailsProps) {
  const key = 'additional-errors'
  const updateContent = () =>
    notificationHandler({
      key,
      title: 'Additional Error Details',
      alternateType: 'error',
      details: getDetails(messages),
      duration: 'infinite',
    })

  notificationHandler({
    key,
    title: message,
    alternateType: 'error',
    details: !!messages?.length ? (
      <Space direction="vertical">
        <Paragraph>There are additional details</Paragraph>
        <Button
          type="primary"
          style={{ float: 'right' }}
          onClick={() => updateContent()}
        >
          Show Details
        </Button>
      </Space>
    ) : undefined,
    onClose: callback,
  })
}
